import { Link, useLocation } from 'react-router-dom';
import { RatingStars } from '../base/RatingStars';
import { filamentCompareData, formatBendStrength } from '../../data/filamentCompare';

interface FilamentComparePanelProps {
  slug: string;
}

const FilamentComparePanel = ({ slug }: FilamentComparePanelProps) => {
  const data = filamentCompareData.find(f => f.slug === slug);
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');

  // テキスト辞書
  const TEXT = isEnglish ? {
    title: 'Performance Rating',
    compareLink: 'Compare All Filaments →',
    description: 'Rated on 5 criteria: print temperature, warping, strength, creep resistance, and heat resistance (★1-5).',
    printTemp: 'Print Temp',
    warping: 'Warping',
    strength: 'Strength',
    creepResistance: 'Creep Resistance',
    heatResistance: 'Heat Resistance',
    nozzle: 'Nozzle',
    bed: 'Bed',
    notEntered: 'Not entered',
    rated: 'Rated',
    notMeasured: 'Not measured',
    bendStrength: 'Bending Strength',
    heatResistanceTemp: 'Heat Resistance Temp'
  } : {
    title: '性能評価',
    compareLink: '全フィラメント比較 →',
    description: '印刷温度、反り、強度、クリープ耐性、耐熱性の5項目を★1〜5で評価しています。',
    printTemp: '印刷温度',
    warping: '反り',
    strength: '強度',
    creepResistance: 'クリープ耐性',
    heatResistance: '耐熱',
    nozzle: 'ノズル',
    bed: 'ベッド',
    notEntered: '未入力',
    rated: '評価済み',
    notMeasured: '未測定',
    bendStrength: '曲げ強度',
    heatResistanceTemp: '耐熱温度'
  };

  if (!data) {
    return null;
  }

  const compareLink = isEnglish ? '/en/filament/compare' : '/filament/compare';

  return (
    <div className="metal-panel p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-semibold text-white font-['Exo'] accent-line pb-2">
          {TEXT.title}
        </h2>
        <Link 
          to={compareLink}
          className="text-accent hover:text-accent-orange transition-colors text-sm whitespace-nowrap"
        >
          {TEXT.compareLink}
        </Link>
      </div>
      
      <p className="text-metal-light text-sm mb-4">
        {TEXT.description}
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* 印刷温度 */}
        <div className="bg-metal p-4 rounded">
          <h3 className="text-white font-semibold mb-2 text-sm">{TEXT.printTemp}</h3>
          <div className="flex items-center space-x-2 mb-2">
            <RatingStars rating={data.printTempRating} />
          </div>
          {data.printTempRating > 0 ? (
            <div className="text-metal-light text-xs space-y-1">
              {data.nozzleTemp && <p>{TEXT.nozzle}: {data.nozzleTemp}</p>}
              {data.bedTemp && <p>{TEXT.bed}: {data.bedTemp}</p>}
            </div>
          ) : (
            <p className="text-metal-light text-xs">{TEXT.notEntered}</p>
          )}
        </div>

        {/* 反り */}
        <div className="bg-metal p-4 rounded">
          <h3 className="text-white font-semibold mb-2 text-sm">{TEXT.warping}</h3>
          <div className="flex items-center space-x-2 mb-2">
            <RatingStars rating={data.warpingRating} />
          </div>
          {data.warpingRating > 0 ? (
            (isEnglish && data.warpingNoteEn) || data.warpingNote ? (
              <p className="text-metal-light text-xs">{isEnglish && data.warpingNoteEn ? data.warpingNoteEn : data.warpingNote}</p>
            ) : (
              <p className="text-metal-light text-xs">{TEXT.rated}</p>
            )
          ) : (
            <p className="text-metal-light text-xs">{TEXT.notEntered}</p>
          )}
        </div>

        {/* 強度 */}
        <div className="bg-metal p-4 rounded">
          <h3 className="text-white font-semibold mb-2 text-sm">{TEXT.strength}</h3>
          <div className="flex items-center space-x-2 mb-2">
            <RatingStars rating={data.strengthRating} />
          </div>
          {data.strengthRating > 0 ? (
            data.bendStrengthMPa ? (
              <p className="text-metal-light text-xs">{TEXT.bendStrength}: {formatBendStrength(data.slug, data.bendStrengthMPa)}</p>
            ) : (
              <p className="text-metal-light text-xs">{TEXT.notMeasured}</p>
            )
          ) : (
            <p className="text-metal-light text-xs">{TEXT.notEntered}</p>
          )}
        </div>

        {/* クリープ耐性 */}
        <div className="bg-metal p-4 rounded">
          <h3 className="text-white font-semibold mb-2 text-sm">{TEXT.creepResistance}</h3>
          <div className="flex items-center space-x-2 mb-2">
            <RatingStars rating={data.creepResistanceRating} />
          </div>
          {data.creepResistanceRating > 0 ? (
            (isEnglish && data.creepNoteEn) || data.creepNote ? (
              <p className="text-metal-light text-xs">{isEnglish && data.creepNoteEn ? data.creepNoteEn : data.creepNote}</p>
            ) : (
              <p className="text-metal-light text-xs">{TEXT.rated}</p>
            )
          ) : (
            <p className="text-metal-light text-xs">{TEXT.notEntered}</p>
          )}
        </div>

        {/* 耐熱 */}
        <div className="bg-metal p-4 rounded">
          <h3 className="text-white font-semibold mb-2 text-sm">{TEXT.heatResistance}</h3>
          <div className="flex items-center space-x-2 mb-2">
            <RatingStars rating={data.heatResistanceRating} />
          </div>
          {data.heatResistanceRating > 0 ? (
            data.heatResistanceTemp ? (
              <p className="text-metal-light text-xs">{TEXT.heatResistanceTemp}: {data.heatResistanceTemp}°C</p>
            ) : (
              <p className="text-metal-light text-xs">{TEXT.notEntered}</p>
            )
          ) : (
            <p className="text-metal-light text-xs">{TEXT.notEntered}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilamentComparePanel;
